import { Ban, User, Car, AlertCircle, Trash2, Loader2 } from 'lucide-react';
import type { Restriction } from '@yawi/types';
import { useDateFormatter } from '@/shared/hooks';

interface RestrictionsListProps {
  restrictions: Restriction[];
  onDelete: (id: string) => void;
  deleting?: boolean;
}

const TYPE_ICONS = {
  PERSON: User,
  VEHICLE: Car,
  GENERAL: AlertCircle,
};

const TYPE_LABELS = {
  PERSON: 'Persona',
  VEHICLE: 'Vehiculo',
  GENERAL: 'General',
};

const TYPE_STYLES = {
  PERSON: 'bg-red-50 text-red-700 border-red-200',
  VEHICLE: 'bg-orange-50 text-orange-700 border-orange-200',
  GENERAL: 'bg-purple-50 text-purple-700 border-purple-200',
};

export const RestrictionsList = ({ restrictions, onDelete, deleting }: RestrictionsListProps) => {
  const { formatDate } = useDateFormatter();

  if (restrictions.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Ban className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No hay restricciones activas</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {restrictions.map((restriction) => {
        const Icon = TYPE_ICONS[restriction.type];
        const typeLabel = TYPE_LABELS[restriction.type];
        const typeStyle = TYPE_STYLES[restriction.type];

        return (
          <div
            key={restriction.id}
            className={`rounded-xl border p-4 ${typeStyle}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white/50 rounded-lg">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium uppercase tracking-wide opacity-70">
                      {typeLabel}
                    </span>
                    {!restriction.active && (
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                        Inactivo
                      </span>
                    )}
                  </div>

                  {restriction.name && (
                    <p className="font-semibold">{restriction.name}</p>
                  )}

                  {restriction.documentId && (
                    <p className="text-sm">
                      <span className="opacity-70">ID:</span> {restriction.documentId}
                    </p>
                  )}

                  {restriction.licensePlate && (
                    <p className="text-sm font-mono">
                      <span className="opacity-70">Placa:</span> {restriction.licensePlate}
                    </p>
                  )}

                  <p className="text-sm mt-2">
                    <span className="opacity-70">Motivo:</span> {restriction.reason}
                  </p>

                  <div className="flex flex-wrap gap-3 mt-2 text-xs opacity-70">
                    <span>Creado: {formatDate(restriction.createdAt)}</span>
                    {restriction.expiresAt && (
                      <span>Expira: {formatDate(restriction.expiresAt)}</span>
                    )}
                    <span>Por: {restriction.createdBy}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => onDelete(restriction.id)}
                disabled={deleting}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors disabled:opacity-50"
                title="Eliminar restriccion"
              >
                {deleting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
